import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BudgetSetupModal = ({ 
  isOpen = false, 
  onClose = () => {}, 
  onSave = () => {},
  currentBudget = 0
}) => {
  const [budgetAmount, setBudgetAmount] = useState(currentBudget || '');
  const [budgetPeriod, setBudgetPeriod] = useState('total');
  const [notificationThreshold, setNotificationThreshold] = useState(80);
  const [errors, setErrors] = useState({});

  const periodOptions = [
    { value: 'total', label: 'Total Budget (One-time)' },
    { value: 'monthly', label: 'Monthly Budget' },
    { value: 'quarterly', label: 'Quarterly Budget' },
    { value: 'yearly', label: 'Yearly Budget' }
  ];

  const thresholdOptions = [
    { value: 50, label: '50% - Early warning' },
    { value: 70, label: '70% - Moderate warning' },
    { value: 80, label: '80% - Standard warning' },
    { value: 90, label: '90% - Late warning' },
    { value: 100, label: '100% - Only when exceeded' }
  ];

  const validateForm = () => {
    const newErrors = {};
    
    if (!budgetAmount || budgetAmount <= 0) {
      newErrors.budgetAmount = 'Please enter a valid budget amount';
    }
    
    if (budgetAmount > 50000) {
      newErrors.budgetAmount = 'Budget amount seems unusually high';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        amount: parseFloat(budgetAmount),
        period: budgetPeriod,
        notificationThreshold: notificationThreshold
      });
      onClose();
    }
  };

  const handleClose = () => {
    setBudgetAmount(currentBudget || '');
    setBudgetPeriod('total');
    setNotificationThreshold(80);
    setErrors({});
    onClose();
  };

  const getBudgetSuggestions = () => {
    const suggestions = [
      { amount: 500, label: 'Starter upgrades' },
      { amount: 1000, label: 'Mid-range improvements' },
      { amount: 2500, label: 'Significant upgrades' },
      { amount: 5000, label: 'Complete overhaul' }
    ];
    
    return suggestions;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-md animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Icon name="Wallet" size={20} />
              <span>Set Budget Limit</span>
            </h3>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-muted rounded-full transition-colors interactive-scale"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Budget Amount */}
            <div>
              <Input
                label="Budget Amount"
                type="number"
                placeholder="Enter amount in USD"
                value={budgetAmount}
                onChange={(e) => setBudgetAmount(e?.target?.value)}
                error={errors?.budgetAmount}
                required
              />
              
              {/* Quick Suggestions */}
              <div className="mt-3">
                <div className="text-sm text-muted-foreground mb-2">Quick suggestions:</div>
                <div className="flex flex-wrap gap-2">
                  {getBudgetSuggestions()?.map((suggestion) => (
                    <button
                      key={suggestion?.amount}
                      onClick={() => setBudgetAmount(suggestion?.amount?.toString())}
                      className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded-full transition-colors interactive-scale"
                    >
                      ${suggestion?.amount} - {suggestion?.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Budget Period */}
            <Select
              label="Budget Period"
              description="Choose how you want to track your spending"
              options={periodOptions}
              value={budgetPeriod}
              onChange={setBudgetPeriod}
            />

            {/* Notification Threshold */}
            <Select
              label="Notification Threshold"
              description="Get notified when you reach this percentage of your budget"
              options={thresholdOptions}
              value={notificationThreshold}
              onChange={setNotificationThreshold}
            />

            {/* Budget Preview */}
            {budgetAmount && (
              <div className="bg-muted/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Budget Summary</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Budget:</span>
                    <span className="font-medium text-foreground">${parseFloat(budgetAmount || 0)?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Period:</span>
                    <span className="font-medium text-foreground">
                      {periodOptions?.find(p => p?.value === budgetPeriod)?.label}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Alert at:</span>
                    <span className="font-medium text-foreground">
                      ${((parseFloat(budgetAmount || 0) * notificationThreshold) / 100)?.toFixed(2)} ({notificationThreshold}%)
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="bg-accent/10 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Icon name="Info" size={16} className="mt-0.5 flex-shrink-0" color="var(--color-accent)" />
                <div className="text-sm text-foreground">
                  <div className="font-medium mb-1">Budget Tips:</div>
                  <ul className="text-muted-foreground space-y-1">
                    <li>• Start with a conservative budget and adjust as needed</li>
                    <li>• Consider setting aside 10-20% for unexpected deals</li>
                    <li>• You can update your budget anytime from the summary panel</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex space-x-3 p-6 border-t border-border">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleSave}
              className="flex-1"
              disabled={!budgetAmount || parseFloat(budgetAmount) <= 0}
            >
              Save Budget
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetSetupModal;